import { makeAutoObservable } from "mobx";
import { log } from "./lib/log";
import { BasicEvent, trackAnyEvent } from "./lib/tracking/track";
import { uuid } from "./lib/utils";
import type { RootStore } from "./store";
import { globalEventBus } from "./lib/events/eventBus";

type PyneTourLifecycleEventPayload = {
  id: string;
};

// This class represents the public API of the Pyne script.
export class PyneClient {
  currentUser: { id: string; traits: Record<string, any> } | null = null;
  anonymousId: string | null = null;
  isReady = false;
  readyCallbacks: Function[] = [];

  constructor(public rootStore: RootStore) {
    this.anonymousId = PyneClient.getAnonymousId();
    this.currentUser = PyneClient.getSafedCurrentUser();
    this.runCommand = this.runCommand.bind(this);
    makeAutoObservable(this, { rootStore: false });
  }

  get currentUserData() {
    return {
      id: this.currentUser?.id,
      ...this.currentUser?.traits,
      anonymousId: this.anonymousId,
    };
  }

  PUBLIC_METHODS = [
    "identify",
    "track",
    "terminateCurrentTour",
    "startTour",
    "onTourStarted",
    "onTourFinished",
    "onTourTerminated",
    "onTourSuspended",
    "onTourUnsuspended",
    "onTourDismissed",
    "onUserMinimizedAvatar",
    "onUserExpandedAvatar",
    "ready",
  ];

  async runCommand(method: string, ...args: unknown[]) {
    if (!this.PUBLIC_METHODS.includes(method)) throw new Error(`Unknown method ${method}`);
    // @ts-ignore
    return this[method](...args);
  }

  // Identify lets you provide a unique ID and traits about a user.
  // Usage: window.pyne('identify', "some-user-id-123", { name: "Michael Bolton", email: "mbolton@initech" });
  // To delete user information (e.g. when a user logs out), you can call: `window.pyne('identify', null)`
  async identify(userId: string | null, traits: Record<string, any>) {
    log("identify", userId, traits);
    this.currentUser = userId ? { id: userId, traits } : null;
    if (userId === null) {
      this.rootStore.storage.clearUserState();
    }
    PyneClient.safeCurrentUser(this.currentUser);
  }

  static getAnonymousId() {
    let id = localStorage.getItem("pyne-anonymous-id");
    if (!id) {
      id = uuid();
      localStorage.setItem("pyne-anonymous-id", id);
    }
    return id;
  }

  static safeCurrentUser(userData: typeof PyneClient.prototype.currentUser) {
    // localStorage.setItem("pyne-current-user", JSON.stringify(userData));
  }

  static getSafedCurrentUser() {
    return null;
    // const userData = localStorage.getItem("pyne-current-user");
    // try {
    //   return userData ? JSON.parse(userData) : null;
    // } catch (e) {
    //   console.error(e);
    //   return null;
    // }
  }

  async track<T extends BasicEvent>(eventName: T["event"], eventData: Exclude<T, "event">) {
    if (this.rootStore.isPreview) return;
    if (this.rootStore.pyneSettings.skipTracking) return;

    log("track", eventData);
    trackAnyEvent(
      {
        event: eventName,
        properties: {
          ...eventData,
        },
      },
      {
        user_id: this.currentUser?.id,
        anonymous_id: this.anonymousId ?? undefined,
      }
    );
  }

  startTour(guideId: string) {
    this.rootStore.tourLifecycleManager.forceStartTour(guideId, "trigger_from_pyne_client", "trigger_from_pyne_client");
  }

  terminateCurrentTour() {
    this.rootStore.tourLifecycleManager.terminateCurrentTourAndLookForOthers("trigger_from_pyne_client");
  }

  onTourStarted(callback: (payload: PyneTourLifecycleEventPayload) => void) {
    this.rootStore.tourLifecycleManager.events.on("tourStarted", ({ payload: { guide } }) => {
      callback({ id: guide.id });
    });
  }

  onTourFinished(callback: (payload: PyneTourLifecycleEventPayload) => void) {
    this.rootStore.tourLifecycleManager.events.on("tourFinished", ({ payload: { guide } }) => {
      callback({ id: guide.id });
    });
  }

  onTourTerminated(callback: (payload: PyneTourLifecycleEventPayload) => void) {
    this.rootStore.tourLifecycleManager.events.on("tourTerminated", ({ payload: { guide } }) => {
      callback({ id: guide.id });
    });
  }

  onTourSuspended(callback: (payload: { id: string }) => void) {
    globalEventBus.on("tourPlayback.suspended", ({ context: { guide } }) => {
      if (!guide) return;
      callback({ id: guide.id });
    });
  }

  onTourUnsuspended(callback: (payload: { id: string }) => void) {
    globalEventBus.on("tourPlayback.unsuspended", ({ context: { guide } }) => {
      if (!guide) return;
      callback({ id: guide.id });
    });
  }

  onTourDismissed(callback: (payload: { id: string }) => void) {
    globalEventBus.on("tourLifecycle.tourDismissed", ({ payload: { guide } }) => {
      callback({ id: guide.id });
    });
  }

  onUserMinimizedAvatar(callback: (payload: { id: string }) => void) {
    globalEventBus.on("avatar.minimizeClicked", ({ context: { guide } }) => {
      if (!guide) return;
      callback({ id: guide.id });
    });
  }

  onUserExpandedAvatar(callback: (payload: { id: string }) => void) {
    globalEventBus.on("avatar.expandClicked", ({ context: { guide } }) => {
      if (!guide) return;
      callback({ id: guide.id });
    });
  }

  ready(callback: () => void) {
    if (this.isReady) {
      setTimeout(() => callback(), 0);
    } else {
      this.readyCallbacks.push(callback);
    }
  }

  async runQueuedCommands() {
    const queue = window.pyne.q ?? [];

    while (queue.length) {
      const [method, ...args] = queue.shift()!;
      await this.runCommand(method, ...args);
    }

    (window.pyne as any).c = ([method, ...args]: any[]) => this.runCommand(method, ...args);
    this.isReady = true;
    this.runReadyCallbacks();
  }

  runReadyCallbacks() {
    for (const cb of this.readyCallbacks) {
      setTimeout(() => cb(), 0);
    }
  }
}
